import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["assetId", "playlistId", "isPublished"];
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext, useEffect, useMemo, useState } from 'react';

/* Utils */
import log from 'utils/log';
import track from 'utils/track';
import { getPublicDownloadUrl } from 'utils/asset/getDownloadUrl';
import { IFRAME_TRACK_OPENS_ASSET_TYPES } from 'utils/asset/constants';
import { useFetchMyAsset, useFetchMyAssetPlaylists } from 'actions/assets';

/**
 * The AssetViewer context provider
 * Takes in an assetId and optional playlistId and isPublished parameter and returns a context-provider that manages data and callbacks for displaying an asset.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export var AssetContext = /*#__PURE__*/React.createContext(null);
export function Provider(_ref) {
  var assetId = _ref.assetId,
    playlistId = _ref.playlistId,
    isPublished = _ref.isPublished,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isIframeContentLoaded = _useState2[0],
    setIsIframeContentLoaded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isReady = _useState4[0],
    setReady = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  var _useFetchMyAsset = useFetchMyAsset({
      id: assetId,
      playlistId: playlistId
    }),
    fetchedAsset = _useFetchMyAsset.data,
    isAssetLoading = _useFetchMyAsset.isLoading;
  var _useFetchMyAssetPlayl = useFetchMyAssetPlaylists(assetId),
    parentPlaylists = _useFetchMyAssetPlayl.items,
    areAssetParentPlaylistsLoading = _useFetchMyAssetPlayl.isLoading;
  var isLoading = isAssetLoading || areAssetParentPlaylistsLoading;
  useEffect(function () {
    if (isLoading) {
      setReady(false);
    } else {
      setReady(true);
    }
  }, [isLoading]);
  var asset = useMemo(function () {
    return !fetchedAsset ? undefined : _objectSpread(_objectSpread({}, fetchedAsset), {}, {
      parentPlaylists: parentPlaylists
    });
  }, [fetchedAsset, parentPlaylists]);
  var params = useMemo(function () {
    return {
      assetId: assetId,
      playlistId: playlistId,
      isPublished: isPublished
    };
  }, [assetId, playlistId, isPublished]);
  var metadata = useMemo(function () {
    return _objectSpread({
      asset: asset
    }, params);
  }, [asset, params]);
  useEffect(function () {
    if (!IFRAME_TRACK_OPENS_ASSET_TYPES.some(function (type) {
      var _metadata$asset;
      return type === ((_metadata$asset = metadata.asset) === null || _metadata$asset === void 0 ? void 0 : _metadata$asset.type);
    })) {
      return;
    }
    if (isIframeContentLoaded) {
      log({
        level: 'info',
        message: 'Iframe content loaded',
        metadata: metadata
      });
    }
    var timeoutId = setTimeout(function () {
      // FYI: this is a case when someone just open their tab
      // it did not load, but they did not close the assetViewer
      if (!isIframeContentLoaded) {
        log({
          message: 'Iframe content not loaded',
          metadata: metadata
        });
      }
    }, 30000);
    return function () {
      if ((isReady || error) && !isIframeContentLoaded) {
        log({
          message: 'Iframe content not loaded',
          metadata: metadata
        });
      }
      clearTimeout(timeoutId);
    };
  }, [isIframeContentLoaded, metadata, playlistId, isReady, error]);
  var callbacks = useMemo(function () {
    return {
      markIframeContentAsLoaded: function markIframeContentAsLoaded() {
        setIsIframeContentLoaded(true);
      },
      onLoad: function onLoad() {
        setError(null);
        setReady(true);
      },
      onError: function onError(err) {
        if (asset) {
          // TODO: this doesn't need to be limited to an error code. We could pass a lot of meta-data for debugging here.
          track('asset', 'error', err.code, +asset.id);
        }
        var dataToLog = _objectSpread(_objectSpread({}, params), {}, {
          error: err,
          errorCode: err.code
        });
        log({
          message: 'Asset load error',
          metadata: dataToLog
        });
        setError(err);
      }
    };
  }, [asset, params]);
  var downloadUrl = useMemo(function () {
    return asset && getPublicDownloadUrl(asset);
  }, [asset]);
  return /*#__PURE__*/_jsx(AssetContext.Provider, _objectSpread({
    value: _objectSpread({
      asset: asset,
      params: params,
      isReady: isReady,
      error: error,
      downloadUrl: downloadUrl
    }, callbacks)
  }, otherProps));
}
export function useAssetViewerContext() {
  var context = useContext(AssetContext);
  if (context === null) {
    throw new Error('useAssetViewerContext must be used within a AssetContext.Provider');
  }
  return context;
}